import { projectData } from "./project.data";
import { ProjectDataEntry, ProjectFieldRequirement, ProjectSectionType, ProjectKey, projectHumanSectionType, projectPageTitle, projectPageDescriptions, projectSectionTitle, projectSectionDescription, ProjectPageName, ProjectSectionName, ProjectFieldType, projectPreviewOptionsEnabled, projectSectionCustomGap, } from "./project.data.dtos";
import { ICFImportMethod, ProjectType, StudyAccessData, StudyImportMethod, StudyTranslationLanguage, } from "./study.dtos";
import { asEnum, willNeverHappen } from "./utils";
export function getProjectKey(projectType, studyType, accessData, pdda) {
    if (projectType === ProjectType.GENERAL_DATA_USE) {
        if (accessData === StudyAccessData.FULL_RECORD_IDENTIFIED) {
            if (pdda) {
                return ProjectKey.GENERALDATAUSE_IDENTIFIED_PDDE;
            }
            else {
                return ProjectKey.GENERALDATAUSE_IDENTIFIED_NONPDDE;
            }
        }
        else if (accessData === StudyAccessData.FULL_RECORD_DEIDENTIFIED) {
            if (!pdda) {
                return ProjectKey.GENERALDATAUSE_DEIDENTIFIED_NONPDDE;
            }
        }
    }
    else if (projectType === ProjectType.AI_DATA_USE) {
        return ProjectKey.GENERALDATAUSE_IDENTIFIED_PDDE;
    }
    throw new Error(`No project key found for projectType: ${projectType}, studyType: ${studyType}, accessData: ${accessData}, pdda: ${pdda}`);
}
export function getProjectKeyFromStudy(study) {
    const { projectType, studyType, accessData, pdda } = study;
    return getProjectKey(projectType, studyType, accessData, pdda);
}
export function getProjectKeyFromStudyOrNull(study) {
    try {
        return getProjectKeyFromStudy(study);
    }
    catch (e) {
        return null;
    }
}
export function getProjectPreviewOptions(projectKey) {
    let options = projectKey ? projectPreviewOptionsEnabled[projectKey] : null;
    if (!options)
        options = { consentFlow: true, participantType: true };
    return options;
}
export function getProjectData(projectKey) {
    if (!projectKey)
        return [];
    const data = [];
    for (const item of projectData) {
        const fieldRequirement = item.fieldRequirements[projectKey];
        if (!fieldRequirement)
            continue;
        const { fieldRequirements, ...fields } = item;
        data.push({ ...fields, fieldRequirement });
    }
    return data;
}
export function getProjectDataForStudy(entries) {
    const pages = [];
    for (const entry of entries) {
        const { pageName, sectionName, isLastAdditionalSection, objId, ...item } = entry;
        let page = pages.find((p) => p.pageName === pageName);
        if (!page) {
            page = {
                pageName,
                title: projectPageTitle[pageName],
                description: projectPageDescriptions[pageName],
                sectionsCompleted: 0,
                progress: 0,
                isCompleted: false,
                sections: [],
            };
            pages.push(page);
        }
        let section = page.sections.find((s) => s.sectionName === sectionName);
        if (!section) {
            section = {
                pageName,
                sectionName,
                title: projectSectionTitle[sectionName],
                description: projectSectionDescription[sectionName],
                sectionGap: projectSectionCustomGap(sectionName),
                sectionType: ProjectSectionType.STANDARD,
                displayType: "",
                isReviewed: false,
                areRequiredFieldsCompleted: false,
                isCompleted: false,
                fields: [],
                objId,
                isLastAdditionalSection,
            };
            page.sections.push(section);
        }
        section.fields.push(item);
    }
    for (const page of pages) {
        for (const section of page.sections) {
            const sectionIncludeField = section.fields.find((f) => f.fieldRequirement === ProjectFieldRequirement.SECTION_INCLUDE);
            if (sectionIncludeField) {
                section.sectionIncludeField = sectionIncludeField.includeFieldName;
                section.sectionIncludeFieldValue = sectionIncludeField.includeValue;
            }
            if (sectionIncludeField) {
                section.sectionType = ProjectSectionType.OPTIONAL;
            }
            else if (section.fields.some((f) => [ProjectFieldRequirement.REQUIRED, ProjectFieldRequirement.FORCE_SECTION_REQUIRED].includes(f.fieldRequirement))) {
                section.sectionType = ProjectSectionType.REQUIRED;
            }
            else if (section.fields.every((f) => f.fieldRequirement === ProjectFieldRequirement.VIEW_ONLY)) {
                section.sectionType = ProjectSectionType.VIEW_ONLY;
            }
            else {
                section.sectionType = ProjectSectionType.STANDARD;
            }
            section.displayType = projectHumanSectionType[section.sectionType];
        }
    }
    return {
        sectionsCompleted: 0,
        sectionsTotal: 0,
        pagesCompleted: 0,
        progress: 0,
        isCompleted: false,
        isAtLeastOneInformedConsentFieldFilled: false,
        requiredInformedConsentFieldNames: [],
        emptyRequiredInformedConsentFields: 0,
        optionalInformedConsentFieldNames: [],
        pages,
    };
}
export function getAdditionalSections(studyAdditionalSections) {
    var _a, _b;
    if (!studyAdditionalSections || studyAdditionalSections.length === 0)
        return [];
    const entries = [];
    for (const [index, studyAdditionalSection] of studyAdditionalSections.entries()) {
        const sectionName = asEnum(ProjectSectionName)(`ADDITIONAL_INFORMATION_${index + 1}`);
        if (!sectionName)
            continue;
        const isLastAdditionalSection = index === studyAdditionalSections.length - 1;
        entries.push(new ProjectDataEntry({
            pageName: ProjectPageName.PATIENT_EXPERIENCE,
            sectionName,
            specialFieldName: "additionalInformationTitle",
            fieldValue: studyAdditionalSection.title,
            objId: studyAdditionalSection.id,
            fieldType: ProjectFieldType.TEXTFIELD,
            label: "Section title",
            placeholder: "What is the title of this section?",
            fieldRequirement: ProjectFieldRequirement.REQUIRED,
            isLastAdditionalSection,
        }));
        entries.push(new ProjectDataEntry({
            pageName: ProjectPageName.PATIENT_EXPERIENCE,
            sectionName,
            specialFieldName: "additionalInformationDescription",
            fieldValue: studyAdditionalSection.description,
            objId: studyAdditionalSection.id,
            fieldType: ProjectFieldType.TEXTFIELD,
            multiline: true,
            minRows: 4,
            label: "Additional information",
            placeholder: "What additional information do you wish to add to the patient experience?",
            fieldRequirement: ProjectFieldRequirement.REQUIRED,
            isLastAdditionalSection,
        }));
        entries.push(new ProjectDataEntry({
            pageName: ProjectPageName.PATIENT_EXPERIENCE,
            sectionName,
            objId: studyAdditionalSection.id,
            includeValue: studyAdditionalSection.included,
            fieldRequirement: ProjectFieldRequirement.SECTION_INCLUDE,
            isLastAdditionalSection,
        }));
        entries.push(new ProjectDataEntry({
            pageName: ProjectPageName.PATIENT_EXPERIENCE,
            sectionName,
            fieldSectionAddImageName: sectionName,
            translatableFieldNames: ["additionalInformation", ProjectSectionName.ADDITIONAL_INFORMATION],
            fieldRequirement: ProjectFieldRequirement.OPTIONAL,
            objId: studyAdditionalSection.id,
            isLastAdditionalSection,
        }));
    }
    return (_b = (_a = getProjectDataForStudy(entries).pages[0]) === null || _a === void 0 ? void 0 : _a.sections) !== null && _b !== void 0 ? _b : [];
}
export function filterIncludeFields(study, fields) {
    return fields.filter((field) => {
        if (field.excludeProjectTypes && study.projectType && field.excludeProjectTypes.includes(study.projectType)) {
            return false;
        }
        if (field.fieldIncludeFields) {
            return field.fieldIncludeFields.every((f) => study[f] === true);
        }
        return true;
    });
}
export function updateProjectDataForStudy(data, study, currentPage, studyAdditionalSections) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    data.sectionsCompleted = 0;
    data.pagesCompleted = 0;
    data.isAtLeastOneInformedConsentFieldFilled = false;
    data.requiredInformedConsentFieldNames = [];
    data.emptyRequiredInformedConsentFields = 0;
    data.optionalInformedConsentFieldNames = [];
    const shouldShowProjectDetailsFieldErrors = (study === null || study === void 0 ? void 0 : study.importMethod) &&
        (currentPage === ProjectPageName.PROJECT_DETAILS || currentPage === ProjectPageName.PROJECT_SETTINGS) &&
        [StudyImportMethod.CLINICAL_TRIALS, StudyImportMethod.PROTOCOL_DOCUMENT].includes(study.importMethod);
    const shouldShowPatientExperienceFieldErrors = (study === null || study === void 0 ? void 0 : study.icfImportMethod) &&
        currentPage === ProjectPageName.PATIENT_EXPERIENCE &&
        [ICFImportMethod.PROTOCOL_DOCUMENT, ICFImportMethod.AI_GENERATION, ICFImportMethod.AI_REGENERATION].includes(study.icfImportMethod);
    for (const page of data.pages) {
        page.sectionsCompleted = 0;
        if (page.pageName === ProjectPageName.PATIENT_EXPERIENCE) {
            const additionalInformationIndex = page.sections.findIndex((section) => section.sectionName === ProjectSectionName.ADDITIONAL_INFORMATION);
            if (additionalInformationIndex > -1) {
                const additionalSections = getAdditionalSections(studyAdditionalSections);
                page.sections.splice(additionalInformationIndex, 1, ...additionalSections);
            }
        }
        for (const section of page.sections) {
            section.isReviewed = study.visitedSections ? study.visitedSections.includes(section.sectionName) : false;
            section.areRequiredFieldsCompleted = true;
            section.sectionIncludeFieldValue = section.sectionIncludeField
                ? study[section.sectionIncludeField] === true
                : section.sectionIncludeFieldValue;
            const filteredFields = filterIncludeFields(study, section.fields);
            for (const field of filteredFields) {
                field.isFilledIn = false;
                field.isRequiredCompleted = false;
                field.isFieldIncluded = field.fieldType !== ProjectFieldType.SUBSECTION && section.sectionIncludeFieldValue !== false;
                if (field.isFieldIncluded) {
                    if (field.fieldName) {
                        const fieldValue = study[field.fieldName];
                        if (field.fieldName === "sitePrimaryContactEmail" || field.fieldName === "sitePrimaryContactLink") {
                            const emailValue = (_a = study.sitePrimaryContactEmail) === null || _a === void 0 ? void 0 : _a.trim();
                            const phoneValue = (_b = study.sitePrimaryContactPhone) === null || _b === void 0 ? void 0 : _b.trim();
                            const linkValue = (_c = study.sitePrimaryContactLink) === null || _c === void 0 ? void 0 : _c.trim();
                            field.isFilledIn = !!emailValue || !!phoneValue || !!linkValue;
                        }
                        else if (Array.isArray(fieldValue)) {
                            field.isFilledIn = fieldValue.some((item) => {
                                if (typeof item === "string") {
                                    return !!(item === null || item === void 0 ? void 0 : item.trim());
                                }
                                else {
                                    return !!item;
                                }
                            });
                        }
                        else if (typeof fieldValue === "string") {
                            field.isFilledIn = !!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.trim());
                        }
                        else {
                            field.isFilledIn = fieldValue !== null && fieldValue !== undefined;
                        }
                    }
                    else if (field.specialFieldName) {
                        if (field.specialFieldName === "dataScopes") {
                            field.isFilledIn = study.fullRecordWithoutRestrictedData === true || study.consentDataResourceScopes.length > 0;
                        }
                        else if (field.specialFieldName === "timelineVisits") {
                            field.isFilledIn = !!((_d = study.timelineVisits) === null || _d === void 0 ? void 0 : _d.some((item) => { var _a, _b; return !!((_a = item.period) === null || _a === void 0 ? void 0 : _a.trim()) && !!((_b = item.description) === null || _b === void 0 ? void 0 : _b.trim()); }));
                        }
                        else if (field.specialFieldName === "healthRecordUpdates") {
                            const isHealthRecordsUpdatesEveryCompleted = typeof study.healthRecordsUpdatesEvery === "number" &&
                                study.healthRecordsUpdatesEvery !== undefined &&
                                study.healthRecordsUpdatesEvery !== null;
                            field.isFilledIn = study.healthRecordsUpdatesEnabled !== true ? true : isHealthRecordsUpdatesEveryCompleted;
                        }
                        else if (field.specialFieldName === "patientRecordRetrievalRecency") {
                            const isPatientRecordRetrievalRecencyEveryCompleted = typeof study.patientRecordRetrievalRecencyEvery === "number" &&
                                study.patientRecordRetrievalRecencyEvery !== undefined &&
                                study.patientRecordRetrievalRecencyEvery !== null;
                            field.isFilledIn =
                                study.patientRecordRetrievalRecencyEnabled !== true ? true : isPatientRecordRetrievalRecencyEveryCompleted;
                        }
                        else if (field.specialFieldName === "sitePrimaryContactPhoneNumber") {
                            const emailValue = (_e = study.sitePrimaryContactEmail) === null || _e === void 0 ? void 0 : _e.trim();
                            const phoneValue = (_f = study.sitePrimaryContactPhone) === null || _f === void 0 ? void 0 : _f.trim();
                            const linkValue = (_g = study.sitePrimaryContactLink) === null || _g === void 0 ? void 0 : _g.trim();
                            field.isFilledIn = !!emailValue || !!phoneValue || !!linkValue;
                        }
                        else if (field.specialFieldName === "organizationIcon") {
                            field.isFilledIn = !!study.organizationIconUrl;
                        }
                        else if (field.specialFieldName === "organizationRectangularIcon") {
                            field.isFilledIn = !!study.organizationRectangularIconUrl;
                        }
                        else if (field.specialFieldName === "purposeOfDataDisclosure") {
                            const purposeOfDataDisclosure = (_h = study.purposeOfDataDisclosure) !== null && _h !== void 0 ? _h : [];
                            if (purposeOfDataDisclosure.includes("Other")) {
                                field.isFilledIn = !!study.purposeOfDataDisclosureOther;
                            }
                            else {
                                field.isFilledIn = purposeOfDataDisclosure.length > 0;
                            }
                        }
                        else if (field.specialFieldName === "additionalInformationTitle") {
                            field.isFilledIn = !!((_j = field.fieldValue) === null || _j === void 0 ? void 0 : _j.trim());
                        }
                        else if (field.specialFieldName === "additionalInformationDescription") {
                            field.isFilledIn = !!((_k = field.fieldValue) === null || _k === void 0 ? void 0 : _k.trim());
                        }
                        else if (field.specialFieldName === "frequentlyAskedQuestions") {
                            field.isFilledIn = true;
                        }
                        else if (field.specialFieldName === "additionalQuestions") {
                            const englishAdditionalSections = (_l = study.additionalSections) === null || _l === void 0 ? void 0 : _l.filter((item) => item.language === StudyTranslationLanguage.en);
                            field.isFilledIn =
                                englishAdditionalSections && englishAdditionalSections.length > 0
                                    ? !!englishAdditionalSections.every((item) => { var _a, _b; return !!((_a = item.title) === null || _a === void 0 ? void 0 : _a.trim()) && !!((_b = item.description) === null || _b === void 0 ? void 0 : _b.trim()); })
                                    : true;
                        }
                        else {
                            return willNeverHappen(field.specialFieldName);
                        }
                    }
                    if (field.fieldRequirement === ProjectFieldRequirement.REQUIRED) {
                        field.isRequiredCompleted = field.isFilledIn;
                        if (!field.isRequiredCompleted) {
                            section.areRequiredFieldsCompleted = false;
                        }
                        field.isRequiredError =
                            !field.isRequiredCompleted &&
                                (section.isReviewed || !!shouldShowProjectDetailsFieldErrors || !!shouldShowPatientExperienceFieldErrors);
                    }
                    if (field.informedConsentFieldNames && field.informedConsentFieldNames.length > 0) {
                        if (field.fieldRequirement === ProjectFieldRequirement.REQUIRED) {
                            data.requiredInformedConsentFieldNames.push(...field.informedConsentFieldNames);
                            if (!field.isFilledIn) {
                                data.emptyRequiredInformedConsentFields += field.informedConsentFieldNames.length;
                            }
                        }
                        else {
                            data.optionalInformedConsentFieldNames.push(...field.informedConsentFieldNames);
                        }
                        if (!data.isAtLeastOneInformedConsentFieldFilled && field.isFilledIn) {
                            data.isAtLeastOneInformedConsentFieldFilled = true;
                        }
                    }
                }
            }
            section.isCompleted = section.isReviewed && section.areRequiredFieldsCompleted;
            if (section.isCompleted)
                page.sectionsCompleted++;
        }
        const sectionsTotal = page.sections.length;
        page.isCompleted = page.sectionsCompleted === page.sections.length;
        page.progress = page.isCompleted ? 100 : Math.min(100, (page.sectionsCompleted / sectionsTotal) * 100);
        if (page.isCompleted)
            data.pagesCompleted++;
        data.sectionsCompleted += page.sectionsCompleted;
        data.sectionsTotal += sectionsTotal;
    }
    data.isCompleted = data.pagesCompleted === data.pages.length;
    data.progress = data.isCompleted ? 100 : Math.min(100, (data.sectionsCompleted / data.sectionsTotal) * 100);
    return data;
}
